"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, Mail, ShieldAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
const faqData = [{
  id: "item-1",
  tag: "GENERAL",
  question: "What are TEK TIDES LABS products intended for?",
  answer: "TEK TIDES LABS products are supplied for research use only. Product information is provided for research identification and purchasing purposes."
}, {
  id: "item-2",
  tag: "RESEARCH USE",
  question: "Are TEK TIDES LABS products intended for human use?",
  answer: "No. Products offered by TEK TIDES LABS are supplied for research use only and are not intended for diagnostic, clinical, therapeutic, human, or veterinary applications."
}, {
  id: "item-3",
  tag: "DOCUMENTATION",
  question: "Are Certificates of Analysis available?",
  answer: "Certificate of Analysis information is available upon request for applicable products. Contact TEK TIDES LABS if you need available product documentation."
}, {
  id: "item-4",
  tag: "DOCUMENTATION",
  question: "How do I request product documentation?",
  answer: "Use the TEK TIDES LABS contact page. If your request relates to a previous order, include the applicable order or lot information when available."
}, {
  id: "item-5",
  tag: "SHIPPING",
  question: "Where does TEK TIDES LABS ship?",
  answer: "TEK TIDES LABS currently accepts eligible orders within the United States. Available shipping options and applicable charges are displayed during checkout."
}, {
  id: "item-6",
  tag: "ORDERS",
  question: "How do I know when my order ships?",
  answer: "When tracking information is available, it will be provided through the shipping or order notification sent to the email address associated with the order."
}, {
  id: "item-7",
  tag: "SUPPORT",
  question: "How can I contact TEK TIDES LABS?",
  answer: "For general questions, contact contact@tektideslabs.com. For questions regarding an existing order, contact orders@tektideslabs.com."
}, {
  id: "item-8",
  tag: "PRODUCT INFORMATION",
  question: "Does TEK TIDES LABS provide dosing, reconstitution, administration, or medical advice?",
  answer: "No. TEK TIDES LABS does not provide medical, dosing, reconstitution, administration, or treatment advice."
}, {
  id: "item-9",
  tag: "ORDERING",
  question: "Do I need to be 21 or older to place an order?",
  answer: "Yes. Customers must confirm that they are at least 21 years old before placing an order through the website."
}];
export default function Questions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.4,
        ease: "easeOut" as const
      }
    }
  };
  return <section id="common-questions" data-nav="light" className="relative overflow-hidden bg-[#f8fafb] text-[#0a1218]">
      {/* Hero */}
      <div className="border-b border-[#dbe6eb]">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-4 py-12 sm:px-6 sm:py-16 md:grid-cols-[0.78fr_1.22fr] md:items-end md:gap-14 lg:px-8 lg:py-20">
          <motion.div initial="hidden" animate="visible" variants={containerVariants}>
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="h-px w-7 bg-[#159fdf]" />
              <span className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#1479a7]">
                Help Center
              </span>
            </div>

            <h1 className="mt-5 max-w-[600px] text-[clamp(3rem,6vw,6.2rem)] font-semibold leading-[0.9] tracking-[-0.065em] text-[#0a1218]">
              Questions,
              <br />
              answered.
            </h1>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={containerVariants} className="max-w-[570px] md:justify-self-end">
            <p className="text-[14px] leading-7 text-[#56636a] sm:text-[15px]">
              Straightforward information about TEK TIDES LABS products,
              ordering, shipping, account requirements, and customer
              support.
            </p>

            <div className="mt-6 flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#87969d]">
              <HelpCircle className="size-3.5 text-[#087db3]" aria-hidden="true" />
              <span>Frequently Asked Questions</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* FAQ */}
      <div className="mx-auto max-w-[1440px] px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-[0.68fr_1.32fr] lg:gap-20">
          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.25
        }} variants={containerVariants} className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#1479a7]">
              Common questions
            </p>

            <h2 className="mt-3 max-w-[390px] text-[clamp(2rem,3.6vw,3.7rem)] font-semibold leading-[0.98] tracking-[-0.055em] text-[#0a1218]">
              The useful
              <br className="hidden sm:block" /> details.
            </h2>

            <p className="mt-5 max-w-[360px] text-[12px] leading-6 text-[#718087]">
              Browse the questions below. For something specific to an
              existing order, contact our support team directly.
            </p>

            <div className="mt-7 hidden border-t border-[#dbe6eb] pt-5 lg:block">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-[#087db3]" aria-hidden="true" />
                <p className="max-w-[330px] text-[10px] leading-5 text-[#718087]">
                  TEK TIDES LABS products are supplied for research use
                  only. Product pages provide the applicable purchasing
                  information for each item.
                </p>
              </div>
            </div>
          </motion.aside>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} variants={containerVariants}>
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full border-t border-[#cfdde3]">
              {faqData.map((item, index) => <AccordionItem key={item.id} value={item.id} className="border-b border-[#dbe6eb] px-0">
                  <AccordionTrigger className="group py-6 text-left hover:no-underline sm:py-7">
                    <div className="grid w-full grid-cols-[32px_1fr] gap-3 pr-4 sm:grid-cols-[42px_1fr] sm:gap-4">
                      <span className="pt-0.5 text-[9px] font-semibold tabular-nums tracking-[0.12em] text-[#9aabb2]">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <div className="text-left">
                        <span className="block text-[8px] font-semibold uppercase tracking-[0.17em] text-[#1479a7]">
                          {item.tag}
                        </span>
                        <span className="mt-2 block max-w-[760px] text-[17px] font-semibold leading-[1.25] tracking-[-0.025em] text-[#172126] transition-colors group-hover:text-[#087db3] sm:text-[21px]">
                          {item.question}
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pb-7 pl-[44px] pr-4 sm:pb-8 sm:pl-[58px]">
                    <p className="max-w-[760px] text-[13px] leading-7 text-[#66767d] sm:text-[14px]">
                      {item.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>
      </div>

      {/* Support CTA */}
      <div className="border-t border-[#dbe6eb] bg-[#eef5f8]">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={containerVariants} className="mx-auto flex max-w-[1440px] flex-col gap-7 px-4 py-10 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8 lg:py-12">
          <div>
            <div className="flex items-center gap-3">
              <Mail className="size-4 text-[#087db3]" aria-hidden="true" />
              <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#1479a7]">
                Customer Support
              </span>
            </div>

            <h2 className="mt-3 text-[clamp(1.8rem,3vw,3rem)] font-semibold leading-none tracking-[-0.05em] text-[#0a1218]">
              Still have a question?
            </h2>

            <p className="mt-3 max-w-[570px] text-[12px] leading-6 text-[#718087]">
              Contact TEK TIDES LABS for general support or questions
              regarding an existing order.
            </p>
          </div>

          <Button asChild className="h-11 w-fit shrink-0 rounded-full bg-[#087db3] px-6 text-[11px] font-semibold text-white shadow-none hover:bg-[#066c9a]">
            <Link to="/ContactPage">Contact TEK TIDES LABS</Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}